// The Genesis Block — the Architect's origin manuscript rendered
// as a book-format long-read. Text-first (transcription) with a
// toggle to view the 7 original scanned manuscript pages.
//
// The chapter structure is derived from `## CHAPTER N` headings in
// the source markdown so the table of contents stays in sync with
// the file automatically.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft,
  BookOpen,
  ScrollText,
  Hash,
  Clock,
  Images,
  FileText,
  ChevronRight,
  ListOrdered,
  X,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Shield,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Parse the manuscript markdown into an ordered list of "sections"
// (chapters + prologue/epilogue). Each section has a heading and
// the raw markdown body between headings. This keeps the table of
// contents and the reading anchors in perfect sync with the file.
function parseSections(md) {
  if (!md) return [];
  const lines = md.split("\n");
  const sections = [];
  let current = null;
  const flush = () => {
    if (current) {
      current.body = current.buf.join("\n").trim();
      delete current.buf;
      sections.push(current);
    }
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Match "## <heading>" — the top-level section anchors.
    const m = line.match(/^##\s+(.+?)\s*$/);
    if (m) {
      flush();
      // Look ahead for a "### <subtitle>" line and an italic epigraph.
      let subtitle = null;
      let epigraph = null;
      let bodyStart = i + 1;
      // consume blank lines
      while (bodyStart < lines.length && !lines[bodyStart].trim()) bodyStart++;
      if (bodyStart < lines.length) {
        const sm = lines[bodyStart].match(/^###\s+(.+?)\s*$/);
        if (sm) {
          subtitle = sm[1];
          bodyStart += 1;
          while (bodyStart < lines.length && !lines[bodyStart].trim())
            bodyStart++;
          if (
            bodyStart < lines.length &&
            /^\*.+\*\s*$/.test(lines[bodyStart].trim())
          ) {
            epigraph = lines[bodyStart].trim().replace(/^\*|\*$/g, "");
            bodyStart += 1;
          }
        }
      }
      current = {
        heading: m[1],
        subtitle,
        epigraph,
        id: slugify(m[1] + (subtitle ? "-" + subtitle : "")),
        buf: [],
      };
      i = bodyStart - 1;
      continue;
    }
    // Section separator "---" is a soft break inside the manuscript
    // — skip it so the divider doesn't render as a chapter.
    if (line.trim() === "---") continue;
    if (current) current.buf.push(line);
  }
  flush();
  return sections;
}

// Book-format markdown components — serif body, elegant blockquotes,
// generous spacing. Different from the /thesis renderer on purpose:
// this is meant to read like a book, not like technical docs.
const bookComponents = {
  h1: ({ children }) => (
    <h3
      className="font-heading text-2xl sm:text-3xl tracking-[0.02em] text-zinc-100 mt-12 mb-4"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      {children}
    </h3>
  ),
  h2: ({ children }) => (
    <h4
      className="font-heading text-xl sm:text-2xl tracking-[0.02em] text-zinc-100 mt-10 mb-3"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      {children}
    </h4>
  ),
  h3: ({ children }) => (
    <h5
      className="text-base sm:text-lg tracking-[0.02em] text-zinc-200 mt-8 mb-3 font-medium"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      {children}
    </h5>
  ),
  p: ({ children }) => (
    <p
      className="text-zinc-300 leading-[1.85] mb-6 text-base sm:text-[17px]"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      {children}
    </p>
  ),
  blockquote: ({ children }) => (
    <blockquote
      className="border-l-2 border-emerald-500/70 pl-6 pr-2 my-8 text-zinc-200 italic text-base sm:text-lg leading-[1.75]"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      {children}
    </blockquote>
  ),
  em: ({ children }) => (
    <em
      className="text-zinc-300 italic"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      {children}
    </em>
  ),
  strong: ({ children }) => (
    <strong className="text-zinc-100 font-semibold">{children}</strong>
  ),
  hr: () => <div className="my-10 flex justify-center text-emerald-500/60">— ✦ —</div>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-emerald-300 hover:text-emerald-100 underline-offset-4 hover:underline"
    >
      {children}
    </a>
  ),
};

// Ornament between chapters — mirrors the "— ✦ —" typography of the
// original manuscript.
function Ornament() {
  return (
    <div
      data-testid="genesis-ornament"
      className="my-12 text-center text-emerald-500/60 text-lg tracking-[0.5em]"
      aria-hidden
    >
      — ✦ —
    </div>
  );
}

// Lightbox for the manuscript pages. Supports keyboard navigation
// (arrow keys + escape) so a reader can flip through the 7 pages
// like a book.
function ManuscriptLightbox({ pages, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (index === null || index === undefined) return null;
  const page = pages[index];
  if (!page) return null;

  return (
    <div
      data-testid="genesis-lightbox"
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-zinc-900">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">
          Page {page.index} of {pages.length} · {page.caption}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          data-testid="genesis-lightbox-close"
          aria-label="Close manuscript viewer"
          className="text-zinc-400 hover:text-emerald-300"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-auto p-4">
        <img
          src={page.url}
          alt={`Manuscript page ${page.index}: ${page.caption}`}
          data-testid={`genesis-lightbox-img-${page.index}`}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full object-contain shadow-2xl shadow-emerald-500/10"
        />
      </div>
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-zinc-900">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          disabled={index === 0}
          data-testid="genesis-lightbox-prev"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-emerald-300 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-4 w-4" /> previous
        </button>
        <div className="text-[10px] font-mono text-zinc-600">
          arrows · esc to close
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          disabled={index === pages.length - 1}
          data-testid="genesis-lightbox-next"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-emerald-300 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          next <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function GenesisBlock() {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [pinInfo, setPinInfo] = useState(null);
  const [showTocButton, setShowTocButton] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const scrollTargetRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/genesis`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, data });
      })
      .catch((e) => {
        if (!cancelled)
          setState({ loading: false, error: String(e), data: null });
      });
    fetch(`${API}/api/accord/genesis-hash`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setPinInfo(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTocButton(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sections = useMemo(
    () => parseSections(state.data?.content),
    [state.data?.content]
  );

  // Prologue = everything above the first "## " heading.
  const prologue = useMemo(() => {
    const md = state.data?.content || "";
    const idx = md.indexOf("\n## ");
    return idx >= 0 ? md.slice(0, idx).trim() : "";
  }, [state.data?.content]);

  const pages = state.data?.manuscript_pages || [];

  // v19.35 · Architect directive: the 7 manuscript pages ARE the Genesis
  // Block experience. Auto-open the reader as soon as the pages load —
  // the transcription below is a fallback for those who close it.
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (!autoOpened && pages.length > 0 && lightboxIdx === null) {
      setLightboxIdx(0);
      setAutoOpened(true);
    }
  }, [pages.length, autoOpened, lightboxIdx]);

  const scrollToToc = () => {
    const el = document.querySelector('[data-testid="genesis-toc"]');
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const nextPage = () =>
    setLightboxIdx((i) => (i === null ? 0 : Math.min(pages.length - 1, i + 1)));
  const prevPage = () =>
    setLightboxIdx((i) => (i === null ? 0 : Math.max(0, i - 1)));

  return (
    <div
      ref={scrollTargetRef}
      className="min-h-screen bg-[#09090b] text-zinc-100"
      data-testid="genesis-page"
    >
      {/* Floating "back to Table of Contents" button */}
      {showTocButton && sections.length > 0 && (
        <button
          type="button"
          onClick={scrollToToc}
          data-testid="genesis-floating-toc-btn"
          aria-label="Return to table of contents"
          className="fixed right-4 sm:right-6 bottom-6 sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2 z-40 group flex items-center gap-2 px-3 py-2.5 sm:px-2.5 sm:py-3 border border-emerald-500/50 bg-[#09090b]/90 backdrop-blur-sm text-emerald-300 hover:bg-emerald-500 hover:text-zinc-950 hover:border-emerald-500 transition-all shadow-lg shadow-emerald-500/10 text-[10px] uppercase tracking-[0.3em]"
        >
          <ListOrdered className="h-4 w-4 flex-shrink-0" />
          <span className="hidden sm:inline group-hover:inline sm:group-hover:inline">
            Chapters
          </span>
        </button>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Back link */}
        <Link
          to="/"
          data-testid="genesis-back"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> home
        </Link>

        {/* Hero */}
        <header
          data-testid="genesis-hero"
          className="border-b border-zinc-900 pb-10 mb-12"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-emerald-300 mb-4">
            <BookOpen className="h-3 w-3" /> Project IVY · The Genesis Block
          </div>
          <h1
            data-testid="genesis-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-[0.03em] leading-[1.05]"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {state.data?.title || "The Genesis Block"}
          </h1>
          {state.data?.subtitle && (
            <p
              data-testid="genesis-subtitle"
              className="text-zinc-400 text-lg sm:text-xl mt-6 max-w-2xl leading-relaxed italic"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              {state.data.subtitle}
            </p>
          )}
          {state.data?.dedication && (
            <p
              data-testid="genesis-dedication"
              className="text-zinc-500 text-sm mt-6 max-w-2xl leading-relaxed"
            >
              {state.data.dedication}
            </p>
          )}
          {state.data && (
            <div
              data-testid="genesis-meta"
              className="mt-6 flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500"
            >
              <span className="inline-flex items-center gap-1.5">
                <FileText className="h-3 w-3" />
                {state.data.chapter_count} chapters
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Images className="h-3 w-3" />
                {state.data.page_count} manuscript pages
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Hash className="h-3 w-3" title={state.data.sha256}>
                </Hash>
                sha {state.data.sha256?.slice(0, 8)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                {formatBytes(state.data.size_bytes)}
              </span>
            </div>
          )}
        </header>

        {/* Loading */}
        {state.loading && (
          <div
            data-testid="genesis-loading"
            className="text-center py-24 text-zinc-500"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] mb-3">
              Opening the manuscript
            </div>
            <div className="inline-block h-4 w-4 border border-emerald-500/40 border-t-emerald-500 rounded-full animate-spin" />
          </div>
        )}

        {/* Error */}
        {state.error && (
          <div
            data-testid="genesis-error"
            className="border border-red-500/40 bg-red-500/[0.03] p-6 my-8 text-sm text-red-300"
          >
            Failed to load the manuscript: {state.error}
          </div>
        )}

        {/* Manuscript images strip — always visible above the fold so
            readers know the physical source exists. Click any thumb
            to open the full-page lightbox. */}
        {pages.length > 0 && (
          <section
            data-testid="genesis-manuscript-strip"
            className="mb-12"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 inline-flex items-center gap-2">
                <ScrollText className="h-3 w-3" /> Original manuscript · {pages.length} pages
              </div>
              <button
                type="button"
                onClick={() => openLightbox(0)}
                data-testid="genesis-open-manuscript"
                className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 hover:text-emerald-300 inline-flex items-center gap-1.5"
              >
                Open reader <ChevronRight className="h-3 w-3" />
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
              {pages.map((p, i) => (
                <button
                  type="button"
                  key={p.index}
                  onClick={() => openLightbox(i)}
                  data-testid={`genesis-thumb-${p.index}`}
                  aria-label={`Open manuscript page ${p.index}`}
                  className="group relative aspect-[3/4] overflow-hidden border border-zinc-800 hover:border-emerald-500/60 bg-[#0b0b0d] transition-colors"
                >
                  <img
                    src={p.url}
                    alt={`Page ${p.index}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity"
                  />
                  <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#09090b] to-transparent px-1.5 py-1 text-[9px] font-mono text-zinc-400 group-hover:text-emerald-300">
                    {String(p.index).padStart(2, "0")}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Table of contents */}
        {sections.length > 0 && (
          <nav
            data-testid="genesis-toc"
            className="border border-zinc-800 bg-[#0b0b0d] p-5 mb-16"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-4">
              Contents
            </div>
            <ol className="space-y-2">
              {sections.map((sec, idx) => (
                <li key={sec.id}>
                  <a
                    href={`#${sec.id}`}
                    data-testid={`genesis-toc-${sec.id}`}
                    className="group flex items-baseline gap-3 text-sm text-zinc-300 hover:text-emerald-300"
                    style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                  >
                    <span className="font-mono text-[11px] text-zinc-600 group-hover:text-emerald-400 w-6">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1">
                      {sec.heading}
                      {sec.subtitle && (
                        <span className="text-zinc-500 font-light italic">
                          {" — "}
                          {sec.subtitle}
                        </span>
                      )}
                    </span>
                    <ChevronRight className="h-3 w-3 text-zinc-700 group-hover:text-emerald-400 flex-shrink-0" />
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Prologue (unchaptered opening) */}
        {prologue && (
          <section
            data-testid="genesis-prologue"
            className="mb-16"
          >
            <ReactMarkdown components={bookComponents}>
              {prologue}
            </ReactMarkdown>
            <Ornament />
          </section>
        )}

        {/* Chapters */}
        {sections.map((sec, idx) => (
          <article
            key={sec.id}
            id={sec.id}
            data-testid={`genesis-section-${sec.id}`}
            className="mb-16 scroll-mt-8"
          >
            <header className="mb-8">
              <div className="text-[10px] uppercase tracking-[0.5em] text-emerald-300 mb-3">
                {sec.heading}
              </div>
              {sec.subtitle && (
                <h2
                  data-testid={`genesis-section-title-${sec.id}`}
                  className="font-heading text-3xl sm:text-4xl tracking-[0.02em] text-zinc-100 leading-tight"
                  style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                >
                  {sec.subtitle}
                </h2>
              )}
              {sec.epigraph && (
                <p
                  className="mt-4 text-zinc-400 italic text-base sm:text-lg leading-relaxed"
                  style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                >
                  {sec.epigraph}
                </p>
              )}
            </header>
            <div className="genesis-body">
              <ReactMarkdown components={bookComponents}>
                {sec.body}
              </ReactMarkdown>
            </div>
            {idx < sections.length - 1 && <Ornament />}
          </article>
        ))}

        {/* Seal / pin status */}
        {pinInfo && (
          <div
            data-testid="genesis-seal"
            className="border border-emerald-500/30 bg-emerald-500/[0.03] p-5 mt-16 text-xs text-zinc-300 leading-relaxed"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-3 inline-flex items-center gap-2">
              <Shield className="h-3 w-3" /> Cryptographic seal
            </div>
            {pinInfo.pinned ? (
              <>
                <p>
                  This Genesis Block is sealed to the Never-Harm chain.
                  Its byte-hash is pinned on-chain against the same
                  HMAC secret as every truth-ledger entry.
                </p>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-[10px] text-zinc-500">
                  <div>pinned {formatDate(pinInfo.pinned_at)}</div>
                  <div>
                    integrity{" "}
                    <span
                      className={
                        pinInfo.matches
                          ? "text-emerald-300"
                          : "text-red-300"
                      }
                    >
                      {pinInfo.matches ? "verified" : "MISMATCH"}
                    </span>
                  </div>
                  <div className="col-span-2 truncate" title={pinInfo.pinned_hash}>
                    hash {pinInfo.pinned_hash?.slice(0, 32)}…
                  </div>
                  <div className="col-span-2 truncate" title={pinInfo.never_harm_event_hash}>
                    chain event {pinInfo.never_harm_event_hash?.slice(0, 32)}…
                  </div>
                </div>
              </>
            ) : (
              <p className="text-zinc-400">
                The Genesis Block manuscript is on disk but has not
                yet been sealed to the Never-Harm chain. The Architect
                signs it with a single request; from that moment
                forward, any modification becomes detectable.
              </p>
            )}
          </div>
        )}

        {/* Closing */}
        {sections.length > 0 && (
          <div className="border-t border-zinc-900 pt-8 mt-12 text-sm text-zinc-500 leading-relaxed">
            <p>
              This manuscript is the starting point. Anything added
              after it is sealed in a new block. To read the primary
              technical documents alongside it, visit{" "}
              <Link
                to="/thesis"
                data-testid="genesis-footer-thesis"
                className="text-emerald-300 hover:text-emerald-100"
              >
                the thesis
              </Link>
              . To verify the on-chain seal, walk the truth ledger from{" "}
              <Link
                to="/panel"
                data-testid="genesis-footer-panel"
                className="text-emerald-300 hover:text-emerald-100"
              >
                the reviewer dossier
              </Link>
              .
            </p>
            <p className="mt-3 font-mono text-xs text-zinc-600">
              GET /api/genesis · GET /api/accord/genesis-hash
            </p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <ManuscriptLightbox
        pages={pages}
        index={lightboxIdx}
        onClose={closeLightbox}
        onPrev={prevPage}
        onNext={nextPage}
      />
    </div>
  );
}
